/**
 * Binding a `<input type="date">` to a CLAMPED value without the clamp fighting the keyboard.
 *
 * A date input emits a complete value on every keystroke of the year, so typing "2026" into a field
 * that already holds a day and month arrives as `0002-…`, then `0020-…`, then `0202-…`, and only
 * finally `2026-…`. The first three are real dates in the distant past. A clamp that rejects past
 * dates therefore returned '' three times on the way to a perfectly good answer — and because the
 * input was controlled, each '' wiped the whole field, day and month with it. Typing the year FIRST
 * looked like it worked only because the value stays incomplete, and so untouched by the clamp, until
 * the final segment is filled.
 *
 * The fix is to separate the DRAFT being typed from the COMMITTED value the engine reads. Every
 * keystroke updates the draft; only a complete date is put through the clamp and committed. On blur
 * the draft snaps to whatever was actually committed, so a rejected date visibly reverts rather than
 * sitting on screen looking accepted.
 */
import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';

/** A complete `YYYY-MM-DD` — whether or not it is a date the clamp would go on to accept. */
export function isCompleteDateValue(raw: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(raw);
}

/**
 * Should this raw input value be put through the clamp and committed yet?
 *
 * Empty means the field was cleared, which IS an answer ("care starts now") and must commit. Anything
 * incomplete is a half-typed date: not an answer, and the thing that must never reach the clamp.
 */
export function shouldCommitDateValue(raw: string): boolean {
    return raw === '' || isCompleteDateValue(raw);
}

/** Props to spread onto a date `<input>`: the draft value plus its change and blur handlers. */
export interface ClampedDateFieldProps {
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
}

export function useClampedDateField(
    committed: string,
    commit: (next: string) => void,
    clamp: (raw: string) => string,
): ClampedDateFieldProps {
    const [draft, setDraft] = useState(committed);
    // The committed value also changes from OUTSIDE the field — restoring a saved strategy, or the
    // care date moving and dragging the RAD sale date back with it — and the draft has to follow.
    useEffect(() => setDraft(committed), [committed]);
    return {
        value: draft,
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            setDraft(raw);
            if (shouldCommitDateValue(raw)) commit(clamp(raw));
        },
        onBlur: () => setDraft(committed),
    };
}
